import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store/root-reducer';

import { validateOtp } from '../domain/login.validation';

const selectLogin = (state: RootState) => state.login;

export const selectPhase = (state: RootState) => selectLogin(state).phase;
export const selectCredentials = (state: RootState) => selectLogin(state).credentials;
export const selectOtp = (state: RootState) => selectLogin(state).otp;
export const selectOtpToken = (state: RootState) => selectLogin(state).otpToken;
export const selectOtpEmail = (state: RootState) => selectLogin(state).otpEmail;
export const selectCredentialsError = (state: RootState) => selectLogin(state).credentialsError;
export const selectOtpError = (state: RootState) => selectLogin(state).otpError;
export const selectRetryAfter = (state: RootState) => selectLogin(state).retryAfter;
export const selectSession = (state: RootState) => selectLogin(state).session;
export const selectResendStatus = (state: RootState) => selectLogin(state).resendStatus;

export const selectIsCredentialsSubmitting = (state: RootState) =>
  selectLogin(state).credentialsStatus === 'submitting';
export const selectIsOtpSubmitting = (state: RootState) =>
  selectLogin(state).otpStatus === 'submitting';

/** Derived: are both credentials fields non-empty? Drives the Sign In button. */
export const selectCanSubmitCredentials = createSelector(
  selectCredentials,
  (c) => c.email !== '' && c.password !== '',
);

/** Derived: is the 6-digit code fully entered? Drives Verify + auto-submit. */
export const selectIsOtpComplete = createSelector(selectOtp, (code) => validateOtp(code));

/**
 * Single user-facing credentials message (transport error first, else the first
 * validation message), mirroring the original page's single Alert.
 */
export const selectCredentialsErrorMessage = createSelector(selectCredentialsError, (error) => {
  if (!error) return undefined;
  if (error.kind === 'validation') return error.fields.email ?? error.fields.password;
  return error.message;
});

/** Single user-facing OTP message (transport/validation). */
export const selectOtpErrorMessage = createSelector(selectOtpError, (error) => {
  if (!error) return undefined;
  if (error.kind === 'validation') return Object.values(error.fields)[0];
  return error.message;
});
